import { findApp, type SuiteAppId } from "@/features/shell/suiteApps";

// Maps an attachment's content type or file extension to the suite app that
// can open it. The Office-365 style "Open in..." action on each attachment
// chip reads this mapping. Until Docs/Sheet/Present/Drive/PDF-viewer ship,
// the target app is marked `comingSoon` and the tile renders disabled.

export interface OpenInTarget {
    appId: SuiteAppId;
    appName: string;
    url: string;
    comingSoon: boolean;
    /** Human-readable reason the target is offered — "open as .docx in Notes" */
    label: string;
}

const CONTENT_TYPE_ROUTES: Array<{ test: RegExp; appId: SuiteAppId; label: string }> = [
    // Word
    { test: /^application\/vnd\.openxmlformats-officedocument\.wordprocessingml/i, appId: "docs", label: "Open in Notes" },
    { test: /^application\/msword$/i, appId: "docs", label: "Open in Notes" },
    { test: /^application\/rtf$/i, appId: "docs", label: "Open in Notes" },
    // Excel
    { test: /^application\/vnd\.openxmlformats-officedocument\.spreadsheetml/i, appId: "sheet", label: "Open in Workbook" },
    { test: /^application\/vnd\.ms-excel$/i, appId: "sheet", label: "Open in Workbook" },
    { test: /^text\/csv$/i, appId: "sheet", label: "Open in Workbook" },
    // PowerPoint
    { test: /^application\/vnd\.openxmlformats-officedocument\.presentationml/i, appId: "present", label: "Open in Present" },
    { test: /^application\/vnd\.ms-powerpoint$/i, appId: "present", label: "Open in Present" },
    // PDF
    { test: /^application\/pdf$/i, appId: "pdfViewer", label: "Open in PDF Reader" }
];

const EXTENSION_ROUTES: Record<string, { appId: SuiteAppId; label: string }> = {
    doc: { appId: "docs", label: "Open in Notes" },
    docx: { appId: "docs", label: "Open in Notes" },
    rtf: { appId: "docs", label: "Open in Notes" },
    odt: { appId: "docs", label: "Open in Notes" },

    xls: { appId: "sheet", label: "Open in Workbook" },
    xlsx: { appId: "sheet", label: "Open in Workbook" },
    csv: { appId: "sheet", label: "Open in Workbook" },
    ods: { appId: "sheet", label: "Open in Workbook" },

    ppt: { appId: "present", label: "Open in Present" },
    pptx: { appId: "present", label: "Open in Present" },
    odp: { appId: "present", label: "Open in Present" },

    pdf: { appId: "pdfViewer", label: "Open in PDF Reader" }
};

export function resolveOpenInTarget(
    contentType: string | null | undefined,
    fileName: string | null | undefined
): OpenInTarget | null {
    if (contentType) {
        const hit = CONTENT_TYPE_ROUTES.find((r) => r.test.test(contentType));
        if (hit) return build(hit.appId, hit.label);
    }
    if (fileName) {
        const ext = fileName.split(".").pop()?.toLowerCase();
        if (ext && EXTENSION_ROUTES[ext]) {
            const hit = EXTENSION_ROUTES[ext];
            return build(hit.appId, hit.label);
        }
    }
    return null;
}

function build(appId: SuiteAppId, label: string): OpenInTarget | null {
    const app = findApp(appId);
    if (!app) return null;
    // The PDF viewer ships as an in-app modal (PdfPreviewDialog) rather
    // than a separate SPA, so it's never "coming soon" even though the
    // standalone tile still is. Callers that get appId === "pdfViewer"
    // back open the modal instead of navigating.
    const comingSoon = appId === "pdfViewer" ? false : (app.comingSoon ?? false);
    return {
        appId,
        appName: app.name,
        url: app.url,
        comingSoon,
        label
    };
}

// Build the URL the target app should load for a specific attachment. Today
// we pass a signed `downloadUrl` on the query string; each target SPA reads
// `?openFile=<url>&fileName=<name>` and fetches the bytes itself. Swap for a
// shared-asset locator once the drive service exists.
export function buildOpenInUrl(
    target: OpenInTarget,
    downloadUrl: string,
    fileName: string
): string {
    const u = new URL(target.url);
    u.searchParams.set("openFile", downloadUrl);
    u.searchParams.set("fileName", fileName);
    return u.toString();
}
